import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { StarRating } from "@/components/StarRating";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronRight, MapPin, Package, Scale, Truck, CalendarDays, ShieldCheck,
  MessagesSquare, ArrowLeft, Lock, Loader2, ShoppingCart,
} from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const brand = useBrand();
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderQty, setOrderQty] = useState(1);
  const [deliveryQuartier, setDeliveryQuartier] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/listings/${id}`)
      .then((r) => {
        setListing(r.data);
        setActivePhoto(0);
        return api.get(`/listings?category=${r.data.category}`);
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => navigate("/catalogue"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const openExchange = () => {
    if (!user) { navigate("/connexion"); return; }
    if (user.role !== "buyer") { toast.info(t("listing.loginToExchange")); return; }
    setMessage(`Bonjour, je suis intéressé(e) par votre annonce « ${listing.title} ». Est-elle toujours disponible ?`);
    setDialogOpen(true);
  };

  const sendExchange = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/conversations", { listing_id: listing.id, message });
      toast.success(t("listing.exchangeStarted"));
      setDialogOpen(false);
      navigate(`/echanges?c=${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const openOrder = () => {
    if (!user) { navigate("/connexion"); return; }
    if (user.role !== "buyer") { toast.info(t("listing.loginToExchange")); return; }
    setOrderQty(1);
    setDeliveryQuartier(user.neighborhood || "");
    setDeliveryAddress([user.neighborhood, user.city, user.region].filter(Boolean).join(", "));
    setDeliveryNote("");
    setOrderOpen(true);
  };

  const createOrder = async () => {
    if (!deliveryAddress.trim()) { toast.error(t("order.addressRequired")); return; }
    setOrdering(true);
    try {
      const { data } = await api.post("/orders", { listing_id: listing.id, quantity: Number(orderQty), neighborhood: deliveryQuartier || user.neighborhood || "", full_address: deliveryAddress, delivery_note: deliveryNote });
      toast.success(t("order.orderCreated"));
      setOrderOpen(false);
      navigate(`/commande/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setOrdering(false); }
  };

  if (loading) {
    return (
      <div className="container-page py-10 grid lg:grid-cols-2 gap-10">
        <Skeleton className="aspect-square rounded-2xl" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" /><Skeleton className="h-6 w-1/3" /><Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }
  if (!listing) return null;

  const photos = listing.photos?.length ? listing.photos : [listing.image];
  const specs = [
    { Icon: Package, label: t("listing.quantity"), value: `${listing.quantity} (${listing.price_unit})` },
    { Icon: Scale, label: t("listing.avgWeight"), value: listing.avg_weight },
    { Icon: MapPin, label: t("listing.region"), value: listing.location },
    { Icon: CalendarDays, label: t("listing.availableFrom"), value: listing.availability_date },
  ];

  return (
    <div className="container-page py-8 sm:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" data-testid="breadcrumb">
        <Link to="/" className="hover:text-primary">{t("nav.home")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/catalogue" className="hover:text-primary">{t("nav.catalogue")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground truncate max-w-[220px]">{listing.title}</span>
      </nav>

      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2 text-muted-foreground hover:text-primary" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("common.back")}
      </Button>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="rounded-2xl overflow-hidden bg-muted border border-border">
            <img src={resolvePhoto(photos[activePhoto])} alt={listing.title} className="w-full h-full object-cover aspect-square" />
          </div>
          {photos.length > 1 && (
            <div className="flex gap-3 mt-3">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActivePhoto(i)} data-testid={`thumb-${i}`}
                  className={`h-16 w-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activePhoto ? "border-primary" : "border-transparent"}`}>
                  <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-primary/10 text-primary border-0">{t(`categories.${listing.category}`)}</Badge>
            <Badge className="bg-secondary/10 text-secondary border-0">{t(`states.${listing.state}`)}</Badge>
            <Badge className="bg-accent/15 text-accent-foreground border-0 flex items-center gap-1">
              {listing.delivery_available
                ? <><Truck className="h-3 w-3" /> {t("listing.deliveryYes")}</>
                : t("listing.deliveryNo")}
            </Badge>
          </div>

          <h1 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-secondary">
            {t(`categories.${listing.category}`)} — {t(`states.${listing.state}`)}
          </h1>

          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-heading font-bold text-3xl text-primary">{formatPrice(listing.price, brand.currencySymbol)}</span>
            <span className="text-muted-foreground">/ {listing.price_unit}</span>
          </div>

          {listing.comment && <p className="mt-5 text-foreground/80 leading-relaxed">{listing.comment}</p>}

          <div className="mt-7 grid grid-cols-2 gap-4">
            {specs.map(({ Icon, label, value }) => (
              <div key={label} className="rounded-xl border border-border p-4 bg-card">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
                <div className="mt-1 font-medium text-secondary text-sm">{value}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-xl border border-border p-4 bg-muted/40">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{t("listing.seller")}</p>
                <Link to={`/profil-vendeur/${listing.seller_id}`} data-testid="seller-profile-link"
                  className="font-heading font-semibold text-secondary flex items-center gap-1.5 hover:text-primary transition-colors">
                  {listing.seller_name}
                  {listing.seller_verified && <ShieldCheck className="h-4 w-4 text-secondary" />}
                </Link>
                {listing.seller_reviews_count > 0 && <StarRating value={listing.seller_rating} count={listing.seller_reviews_count} size={13} className="mt-1" />}
              </div>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5">
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {t("listing.exchangeHint")}
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button size="lg" onClick={openOrder} data-testid="order-btn"
              className="flex-1 rounded-full bg-primary hover:bg-primary/90 h-12 text-base">
              <ShoppingCart className="h-5 w-5 mr-2" /> {t("order.orderNow")}
            </Button>
            <Button size="lg" variant="outline" onClick={openExchange} data-testid="exchange-btn"
              className="flex-1 rounded-full h-12 text-base border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
              <MessagesSquare className="h-5 w-5 mr-2" /> {t("listing.exchange")}
            </Button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading font-bold text-2xl text-secondary mb-6">{t("listing.relatedTitle")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p, i) => <ListingCard key={p.id} listing={p} index={i} />)}
          </div>
        </section>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="exchange-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("listing.exchange")} — {listing.seller_name}</DialogTitle>
            <DialogDescription>{t("listing.exchangeHint")}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("messages.about")} : {listing.title}</p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="exchange-message" className="mt-2" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={sendExchange} disabled={sending || !message.trim()} data-testid="exchange-send" className="bg-primary hover:bg-primary/90">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("common.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
        <DialogContent data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-secondary">{t("order.orderTitle")}</DialogTitle>
            <DialogDescription>{listing.title} · {formatPrice(listing.price, brand.currencySymbol)} / {listing.price_unit}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-semibold text-secondary">{t("order.quantity")}</label>
            <Input type="number" min={1} value={orderQty} onChange={(e) => setOrderQty(e.target.value)} className="mt-1.5" data-testid="order-qty" />
            <div className="mt-3">
              <label className="text-sm font-semibold text-secondary">{t("order.deliveryAddress")}</label>
              <Textarea value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Ex : Badalabougou, rue 12, près de la pharmacie…" rows={3} className="mt-1.5" data-testid="order-address" />
              <p className="text-xs text-muted-foreground mt-1">{t("order.addressHint")}</p>
            </div>
            <div className="mt-3">
              <label className="text-sm font-semibold text-secondary">{t("order.deliveryNote")}</label>
              <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="Repères pour le livreur…" className="mt-1.5" data-testid="order-delivery-note" />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{t("order.deliveryPending")}</p>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3">
              <span className="text-sm text-muted-foreground">{t("order.total")}</span>
              <span className="font-heading font-bold text-xl text-primary" data-testid="order-total">
                {formatPrice(listing.price * Math.max(1, Number(orderQty) || 1), brand.currencySymbol)}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={createOrder} disabled={ordering} data-testid="order-confirm" className="bg-primary hover:bg-primary/90">
              {ordering && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("order.orderNow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
